// Pure display helpers for the teacher-facing referral status page
// (src/routes/Referral). Kept free of any React/DOM dependency so they can be
// unit-tested directly — see referralProgress.test.js.
//
// This is a deliberately separate, local label dict from
// src/components/AdminTools/Referral/referralConstants.js (the admin tool's
// enum labels for a <select>) rather than a shared import: that file is owned
// by the admin-tool work happening in parallel, and the wording a teacher
// reads ("14 trial days") is a different concern from an admin form option
// ("Trial Days").

const REWARD_TYPE_LABELS = {
  trial_days: 'Trial days',
  swag_credit: 'Swag credit',
  promo_code: 'Promo code',
  template: 'Template',
};

/**
 * @param {string} rewardType
 * @returns {string} A human label for a reward_type key, falling back to the raw key
 *   for anything not in the dict so an unrecognized type never renders blank.
 */
export function rewardTypeLabel(rewardType) {
  return REWARD_TYPE_LABELS[rewardType] || rewardType;
}

/**
 * Format a bare reward amount (no unit word) — e.g. for a balances table where the
 * reward type is already shown as a column label. Currency-aware for swag_credit;
 * every other type is shown as a plain number.
 *
 * @param {string} rewardType
 * @param {number|string|null|undefined} amount
 * @returns {string|null} null when there's no amount to show (e.g. promo_code/template,
 *   whose reward_amount is always null — see buildRewardFields in the admin helpers).
 */
export function formatRewardNumber(rewardType, amount) {
  if (amount === null || amount === undefined) return null;
  if (rewardType === 'swag_credit') return `$${Number(amount).toFixed(2)}`;
  return `${amount}`;
}

/**
 * Format a reward amount as a full noun phrase — e.g. "14 trial days",
 * "$25.00 swag credit" — for use inline in a sentence (a tier row, a next-tier hint).
 * Falls back to just the type label when there's no amount to attach a number to.
 *
 * @param {string} rewardType
 * @param {number|string|null|undefined} amount
 * @returns {string}
 */
export function formatRewardAmount(rewardType, amount) {
  const number = formatRewardNumber(rewardType, amount);
  if (number === null) return rewardTypeLabel(rewardType);
  if (rewardType === 'trial_days') {
    return `${number} trial day${Number(amount) === 1 ? '' : 's'}`;
  }
  if (rewardType === 'swag_credit') return `${number} swag credit`;
  return `${number} ${rewardTypeLabel(rewardType).toLowerCase()}`;
}

// Mirrors app/controllers/referral_controller.py's STEP_COLUMNS: 'invited' fires as
// soon as the referrer sends an email invite (no action required from the invitee —
// and it never fires at all for a share-link referral, which has no invited_at),
// 'signed_up' when the referred teacher creates an account, 'generated' when they
// create their first resource.
const QUALIFYING_STEP_DESCRIPTIONS = {
  invited: 'when you send someone an invite',
  signed_up: 'when someone you refer signs up for Diffit',
  generated: 'when someone you refer creates a resource',
};

/**
 * @param {string} qualifyingStep
 * @returns {string} A human sentence fragment describing when a referral counts,
 *   falling back to the raw value for anything not in the dict.
 */
export function qualifyingStepDescription(qualifyingStep) {
  return QUALIFYING_STEP_DESCRIPTIONS[qualifyingStep] || qualifyingStep;
}

/**
 * Progress toward a program's next tier, as a percentage — for the progress bar in a
 * collapsed ProgramProgressCard header. Measured against the next tier's threshold
 * (not the highest tier) so the bar reflects the goal a teacher is actually working
 * toward rather than crawling for the whole ladder.
 *
 * @param {{qualified_count: number, next_tier: {threshold: number}|null, tiers: Array}} program
 * @returns {number|null} 0-100, 100 when every tier is achieved, or null when the
 *   program defines no tiers at all (nothing to show progress against).
 */
export function programProgressPercent(program) {
  const tiers = program?.tiers || [];
  if (tiers.length === 0) return null;

  const threshold = program?.next_tier?.threshold;
  // No next tier but tiers exist => the whole ladder is done.
  if (!threshold) return 100;

  const count = Number(program?.qualified_count) || 0;
  const percent = (count / threshold) * 100;
  return Math.max(0, Math.min(100, Math.round(percent)));
}

/**
 * The compact "what's next" phrase for a collapsed card header — e.g.
 * "2 more for 30 trial days".
 *
 * @param {{next_tier: {reward_type, reward_amount, referrals_remaining}|null}} program
 * @returns {string|null} null when there's no next tier.
 */
export function nextTierChip(program) {
  const nextTier = program?.next_tier;
  if (!nextTier) return null;
  const { referrals_remaining, reward_type, reward_amount } = nextTier;
  return `${referrals_remaining} more for ${formatRewardAmount(reward_type, reward_amount)}`;
}

/**
 * The single program the referral page shows: the one `featured_program_id` on
 * /referral/me names — i.e. whatever the referral-program-configuration flag points the
 * `bts_2026_september_scramble` key at. `programs` still carries every program the teacher
 * is assigned to (OnboardingWizardBTS2026 needs that), so the page selects rather than
 * assuming the list is already narrowed.
 *
 * @param {Array<{id: string}>|null|undefined} programs
 * @param {string|null|undefined} featuredProgramId
 * @returns {object|null} null when the key resolves to nothing (unconfigured, or the
 *   program is inactive/out of window), or when the teacher isn't assigned to it — in
 *   both cases the page has no program to show rather than falling back to another one.
 */
export function selectFeaturedProgram(programs, featuredProgramId) {
  if (!featuredProgramId) return null;
  return (programs || []).find((program) => program.id === featuredProgramId) || null;
}
